import type { FC } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Layers,
  Activity,
  Server,
  FolderGit2,
  Terminal,
  Mail,
  Megaphone,
  CreditCard,
  Key,
  ShieldAlert,
  ShieldCheck,
  Gauge,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionsContext';

export const Sidebar: FC = () => {
  const { user } = useAuth();
  const { canAccessRoute } = usePermissions();

  const allNavItems = [
    { to: '/', label: 'Overview', icon: Layers },
    { to: '/webmail', label: 'Webmail Client', icon: Mail },
    { to: '/campaigns', label: 'Email Campaigns', icon: Megaphone },
    { to: '/deliverability', label: 'Deliverability & Warmup', icon: Gauge },
    { to: '/billing', label: 'Billing & Quotas', icon: CreditCard },
    { to: '/api-keys', label: 'API Keys', icon: Key },
    { to: '/uam', label: 'Access Control (UAM)', icon: ShieldCheck },
    { to: '/health', label: 'Cluster Diagnostics', icon: Activity },
  ];

  if (user?.isSuperAdmin) {
    allNavItems.push({ to: '/admin', label: 'Platform Admin', icon: ShieldAlert });
  }

  // Dynamically filter sidebar items to only show screens granted in UAM for user's role
  const navItems = allNavItems.filter((item) => canAccessRoute(item.to));

  return (
    <aside className="w-64 border-r border-slate-800 bg-slate-900/40 p-4 flex flex-col justify-between hidden md:flex">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3 px-3">
          Core Monorepo
        </div>
        <nav className="space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-brand-600/15 text-brand-300 border border-brand-500/20'
                      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40'
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="mt-8">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3 px-3">
            Service Topology
          </div>
          <div className="space-y-2 px-3 text-xs text-slate-400">
            <div className="flex items-center justify-between py-1 border-b border-slate-800/50">
              <span className="flex items-center gap-2">
                <Server className="w-3.5 h-3.5 text-blue-400" />
                <span>API Gateway</span>
              </span>
              <span className="text-[10px] text-slate-400 font-mono">:4000</span>
            </div>
            <div className="flex items-center justify-between py-1 border-b border-slate-800/50">
              <span className="flex items-center gap-2">
                <Terminal className="w-3.5 h-3.5 text-amber-400" />
                <span>Worker Pool</span>
              </span>
              <span className="text-[10px] text-slate-400 font-mono">BullMQ</span>
            </div>
            <div className="flex items-center justify-between py-1">
              <span className="flex items-center gap-2">
                <FolderGit2 className="w-3.5 h-3.5 text-emerald-400" />
                <span>Prisma DB</span>
              </span>
              <span className="text-[10px] text-slate-400 font-mono">PG 16</span>
            </div>
          </div>
        </div>
      </div>

      <div className="p-3 bg-slate-900/80 rounded-xl border border-slate-800 text-xs text-slate-400">
        <p className="font-semibold text-slate-300">Phase 1 Foundation</p>
        <p className="text-[11px] mt-0.5 text-slate-400">CHUNK 02A initialized</p>
      </div>
    </aside>
  );
};
